import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@artstorefronts/ui';
import { ArrowLeft, Loader2, Save, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import * as LucideIcons from 'lucide-react';

import AdminLayout from '../../../components/AdminLayout';
import { ADMIN_UPDATE_USER, ADMIN_DELETE_USER } from '../../../graphql/mutations';

const USER_QUERY = gql`
  query GetUser($asfId: ID!) {
    user(asfId: $asfId) {
      asfId
      firstName
      lastName
      email
      brandVoice
      story
      permissions {
        id
        key
      }
      signupSource
      createdAt
      updatedAt
    }
    userBadges(userId: $asfId) {
      id
      badge {
        id
        name
        color
        icon
      }
      assignedAt
    }
    badges {
      id
      name
      color
      icon
      description
      isArchived
    }
  }
`;

const ADMIN_TOGGLE_USER_BADGE = gql`
  mutation AdminToggleUserBadge($userId: ID!, $badgeId: ID!) {
    adminToggleUserBadge(userId: $userId, badgeId: $badgeId) {
      id
      badge {
        id
        name
        color
        icon
      }
      assignedAt
    }
  }
`;

export default function UserEditPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, loading, error } = useQuery(USER_QUERY, {
    variables: { asfId: id },
    fetchPolicy: 'cache-and-network',
  });

  const [adminUpdateUser] = useMutation(ADMIN_UPDATE_USER);
  const [adminDeleteUser] = useMutation(ADMIN_DELETE_USER);
  const [adminToggleUserBadge] = useMutation(ADMIN_TOGGLE_USER_BADGE);

  const user = data?.user;
  const userBadges = data?.userBadges || [];
  const allBadges = (data?.badges || []).filter((b: any) => !b.isArchived);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setBrandVoice(user.brandVoice || '');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      await adminUpdateUser({
        variables: {
          input: {
            asfId: user.asfId,
            firstName: firstName.trim() || null,
            lastName: lastName.trim() || null,
            brandVoice: brandVoice.trim() || null,
          },
        },
      });
      toast.success(t('users.toast.updated'));
      navigate('/admin/users');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('users.toast.update-failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogChange = (open: boolean) => {
    setShowDeleteDialog(open);
    if (!open) {
      setConfirmEmail('');
    }
  };

  const handleDeleteUser = async () => {
    if (!user || confirmEmail !== user.email) {
      return;
    }

    setIsDeleting(true);
    try {
      await adminDeleteUser({
        variables: {
          asfId: user.asfId,
        },
      });
      toast.success(t('users.toast.deleted'));
      navigate('/admin/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('users.toast.delete-failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setShowDeleteDialog(false);
    setConfirmEmail('');
  };

  const handleToggleBadge = async (badgeId: string) => {
    if (!user) return;

    try {
      await adminToggleUserBadge({
        variables: {
          userId: user.asfId,
          badgeId,
        },
        refetchQueries: ['GetUser'],
      });
      toast.success(t('users.toast.badge-updated'));
    } catch (error: any) {
      console.error('Error toggling badge:', error);
      toast.error(error.message || t('users.toast.badge-update-failed'));
    }
  };

  const renderIcon = (iconName: string, className?: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    if (!IconComponent) {
      return <LucideIcons.Shield className={className} />;
    }
    return <IconComponent className={className} />;
  };

  const isBadgeAssigned = (badgeId: string) => {
    return userBadges.some((ub: any) => ub.badge.id === badgeId);
  };

  if (loading) {
    return (
      <AdminLayout title={t('users.edit.loading-title')} description={t('users.edit.loading-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout title={t('users.edit.not-found-title')} description={t('users.edit.not-found-description')}>
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error ? error.message : t('users.edit.not-found-body')}</p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button asChild variant="outline">
            <Link to="/admin/users" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('users.edit.back-to-users')}
            </Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={t('users.edit.page-title')}
      description={t('users.edit.page-description', { email: user.email })}
      action={
        <Button asChild variant="outline">
          <Link to="/admin/users" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {t('users.edit.back-to-users')}
          </Link>
        </Button>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* User Information (Read-only) */}
        <div className="bg-white border rounded-md p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">{t('users.edit.user-information')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('users.edit.field-asf-id')}</p>
              <p className="font-mono">{user.asfId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.edit.field-email')}</p>
              <p>{user.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.edit.field-created')}</p>
              <p>{format(new Date(user.createdAt), 'MMM d, yyyy h:mm a')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.edit.field-updated')}</p>
              <p>{format(new Date(user.updatedAt), 'MMM d, yyyy h:mm a')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.edit.field-signup-source')}</p>
              <p className="font-mono">{user.signupSource ?? t('users.edit.signup-source-unknown')}</p>
            </div>
            {user.story && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">{t('users.edit.field-story')}</p>
                <p className="text-sm bg-gray-50 p-3 rounded-md">{user.story}</p>
              </div>
            )}
          </div>
        </div>

        {/* Badges Section */}
        <div className="bg-white border rounded-md p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">{t('users.edit.badges-heading')}</h2>
          <div className="flex flex-wrap gap-2">
            {allBadges.length === 0 ? (
              <p className="text-sm text-gray-500">{t('users.edit.no-badges')}</p>
            ) : (
              allBadges.map((badge: any) => {
                const isAssigned = isBadgeAssigned(badge.id);
                return (
                  <button
                    key={badge.id}
                    type="button"
                    onClick={() => handleToggleBadge(badge.id)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      isAssigned ? 'text-white' : 'bg-transparent text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                    style={
                      isAssigned
                        ? {
                            backgroundColor: badge.color,
                          }
                        : undefined
                    }
                    title={badge.description || badge.name}
                  >
                    {renderIcon(badge.icon, 'w-4 h-4')}
                    <span>{badge.name}</span>
                  </button>
                );
              })
            )}
          </div>
          <p className="mt-3 text-xs text-gray-500">{t('users.edit.badges-hint')}</p>
        </div>

        {/* Editable Form */}
        <div className="bg-white border rounded-md p-6">
          <h2 className="text-lg font-medium mb-4">{t('users.edit.edit-details')}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.edit.label-first-name')}
              </label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t('users.edit.placeholder-first-name')}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.edit.label-last-name')}
              </label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('users.edit.placeholder-last-name')}
              />
            </div>

            <div>
              <label htmlFor="brandVoice" className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.edit.label-brand-voice')}
              </label>
              <Textarea
                id="brandVoice"
                value={brandVoice}
                onChange={(e) => setBrandVoice(e.target.value)}
                placeholder={t('users.edit.placeholder-brand-voice')}
                rows={4}
                className="rounded-md"
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{tc('saving')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    <span>{tc('save-changes')}</span>
                  </div>
                )}
              </Button>
            </div>
          </form>
        </div>

        <GiftSubscriptionAllowanceCard userId={user.asfId} />

        {/* Danger Zone */}
        <div className="bg-white rounded-md border-2 border-red-600 p-6 mt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="size-5" />
            <h3 className="text-lg font-semibold">{t('users.edit.danger-zone')}</h3>
          </div>
          <p className="mt-2 text-sm text-red-600">{t('users.edit.danger-zone-description')}</p>
          <Button
            size="lg"
            type="button"
            variant="destructive"
            className="mt-4"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="size-4" />
            {t('users.edit.delete-user')}
          </Button>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={handleDialogChange}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('users.edit.delete-dialog.title')}</DialogTitle>
              <DialogDescription>{t('users.edit.delete-dialog.description')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                {t('users.edit.delete-dialog.confirm-instruction', { email: user.email })}
              </p>
              <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isDeleting}>
                {tc('cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteUser}
                disabled={confirmEmail !== user.email || isDeleting}
              >
                {isDeleting ? tc('deleting') : tc('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

const USER_GIFT_ALLOWANCE = gql`
  query UserGiftSubscriptionAllowance($userId: ID!) {
    userGiftSubscriptionAllowance(userId: $userId) {
      limit
      used
      remaining
    }
  }
`;

const SET_USER_GIFT_ALLOWANCE = gql`
  mutation SetUserGiftSubscriptionAllowance($userId: ID!, $allowance: Int) {
    setUserGiftSubscriptionAllowance(userId: $userId, allowance: $allowance) {
      limit
      used
      remaining
    }
  }
`;

// Per-user override of the global gift allowance — how the exclusivity gets relaxed for a
// specific artist ("let this one give more"). Self-contained: reads the effective allowance,
// sets an override, or clears it back to the default.
function GiftSubscriptionAllowanceCard({ userId }: { userId: string }) {
  const { data, refetch } = useQuery(USER_GIFT_ALLOWANCE, { variables: { userId }, fetchPolicy: 'cache-and-network' });
  const [setAllowance, { loading: saving }] = useMutation(SET_USER_GIFT_ALLOWANCE);
  const [draft, setDraft] = useState('');

  const state = data?.userGiftSubscriptionAllowance;
  useEffect(() => {
    if (state) setDraft(String(state.limit));
  }, [state?.limit]);

  const save = async (allowance: number | null) => {
    await setAllowance({ variables: { userId, allowance } });
    await refetch();
    toast.success('Gift allowance updated');
  };

  return (
    <div className="bg-white rounded-md border p-6 mt-6">
      <h3 className="text-lg font-semibold">Gift subscription allowance</h3>
      <p className="mt-1 text-sm text-gray-500">
        How many Human Made Art subscriptions this artist can gift through referrals. Raise it to let them give more
        once they&apos;ve used their default; clear it to return to the site-wide default.
      </p>
      {state && (
        <p className="mt-2 text-sm text-gray-700">
          Limit <strong>{state.limit}</strong> · used <strong>{state.used}</strong> · remaining{' '}
          <strong>{state.remaining}</strong>
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          type="number"
          min={0}
          step={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-32"
        />
        <Button
          type="button"
          disabled={saving || draft === '' || !Number.isInteger(Number(draft)) || Number(draft) < 0}
          onClick={() => save(Number(draft))}
        >
          Set override
        </Button>
        <Button type="button" variant="outline" disabled={saving} onClick={() => save(null)}>
          Reset to default
        </Button>
      </div>
    </div>
  );
}
